import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { usePermission } from "../hooks/usePermission";
import InviteModal from "../components/InviteModal";
import ConfirmModal from "../components/ConfirmModal";
import { apiErrorMessage } from "../utils/timesheets";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString("nl-BE") : "—");
const fullName = (u) => [u.first_name, u.last_name].filter(Boolean).join(" ");

const INVITE_BADGES = {
  pending: { className: "bg-yellow-100 text-yellow-800", label: "Openstaand" },
  accepted: { className: "bg-green-100 text-green-800", label: "Geaccepteerd" },
  expired: { className: "bg-gray-100 text-gray-800", label: "Verlopen" },
  revoked: { className: "bg-red-100 text-red-800", label: "Ingetrokken" },
};

function RoleChips({ roles }) {
  if (!roles || roles.length === 0) return <span className="text-sm text-gray-400">Geen rollen</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span key={r.id} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800">{r.name}</span>
      ))}
    </div>
  );
}

/** Lijst van gebruikers; een rij opent de detailpagina. Uitnodigingen laden pas wanneer dat tabblad geopend wordt. */
export default function UserList() {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canManageUsers = hasPermission("users.manage");

  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState(null); // null = nog niet geladen
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [deletingInvite, setDeletingInvite] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });

  useEffect(() => {
    apiFetch("/users")
      .then(setUsers)
      .catch((err) => setMessage({ text: apiErrorMessage(err, "Fout bij ophalen gebruikers"), type: "error" }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!message.text) return undefined;
    const timer = setTimeout(() => setMessage({ text: "", type: "" }), 3500);
    return () => clearTimeout(timer);
  }, [message]);

  const loadInvites = useCallback(async () => {
    try {
      setInvites(await apiFetch("/invites"));
    } catch (err) {
      setInvites([]);
      setMessage({ text: apiErrorMessage(err, "Fout bij ophalen uitnodigingen"), type: "error" });
    }
  }, []);

  useEffect(() => {
    if (tab === "invites" && invites === null && canManageUsers) loadInvites();
  }, [tab, invites, canManageUsers, loadInvites]);

  const openInvite = async () => {
    setShowInvite(true);
    if (roles.length === 0) {
      try {
        setRoles(await apiFetch("/roles"));
      } catch (err) {
        setMessage({ text: apiErrorMessage(err, "Fout bij ophalen rollen"), type: "error" });
      }
    }
  };

  const roleOptions = useMemo(() => {
    const seen = new Map();
    users.forEach((u) => u.roles.forEach((r) => seen.set(r.id, r.name)));
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], "nl"));
  }, [users]);

  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter && !u.roles.some((r) => String(r.id) === roleFilter)) return false;
      if (!q) return true;
      return `${fullName(u)} ${u.email || ""} ${u.roles.map((r) => r.name).join(" ")}`.toLowerCase().includes(q);
    });
  }, [users, search, roleFilter]);

  const revokeInvite = async (id) => {
    try {
      await apiFetch(`/invites/${id}`, { method: "DELETE" });
      setMessage({ text: "Uitnodiging ingetrokken", type: "success" });
      loadInvites();
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, "Fout bij intrekken uitnodiging"), type: "error" });
    }
  };

  const resendInvite = async (id) => {
    try {
      const result = await apiFetch(`/invites/${id}/resend`, { method: "POST" });
      await navigator.clipboard.writeText(result.invite_url);
      setMessage({ text: "Nieuwe uitnodigingslink gekopieerd naar het klembord", type: "success" });
      loadInvites();
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, "Fout bij opnieuw versturen uitnodiging"), type: "error" });
    }
  };

  const deleteInvite = async () => {
    const invite = deletingInvite;
    setDeletingInvite(null);
    try {
      await apiFetch(`/invites/${invite.id}/permanent`, { method: "DELETE" });
      setMessage({ text: "Uitnodiging verwijderd", type: "success" });
      loadInvites();
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, "Fout bij verwijderen uitnodiging"), type: "error" });
    }
  };

  const th = "px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider";

  return (
    <div className="p-6">
      {showInvite && (
        <InviteModal roles={roles} onClose={() => setShowInvite(false)} onSuccess={() => { setInvites(null); setTab("invites"); }} />
      )}
      {deletingInvite && (
        <ConfirmModal
          title="Uitnodiging verwijderen"
          message={`De uitnodiging voor ${deletingInvite.email} wordt definitief verwijderd.`}
          confirmLabel="Verwijderen"
          danger
          onConfirm={deleteInvite}
          onCancel={() => setDeletingInvite(null)}
        />
      )}

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Gebruikers</h1>
        {canManageUsers && (
          <button onClick={openInvite} className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            + Gebruiker uitnodigen
          </button>
        )}
      </div>

      {message.text && (
        <div role="status" className={`mb-4 rounded border px-6 py-4 text-sm ${message.type === "success" ? "border-green-200 bg-green-50 text-green-800" : "border-red-200 bg-red-50 text-red-700"}`}>
          {message.text}
        </div>
      )}

      {canManageUsers && (
        <div className="mb-4 flex gap-6 border-b border-gray-200" role="tablist">
          {[["users", "Gebruikers"], ["invites", "Uitnodigingen"]].map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`-mb-px border-b-2 px-1 pb-2 text-sm font-medium ${tab === key ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-800"}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {tab === "users" && (
        <>
          <div className="mb-3 flex flex-wrap gap-3">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek op naam, e-mail of rol…"
              aria-label="Zoeken"
              className="w-72 rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              aria-label="Filter op rol"
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Alle rollen</option>
              {roleOptions.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>

          <div className="overflow-x-auto bg-white shadow rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className={th}>Naam</th>
                  <th className={th}>E-mail</th>
                  <th className={th}>Rollen</th>
                  <th className={th}>Status</th>
                  <th className={th}>Laatste login</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading &&
                  [0, 1, 2].map((i) => (
                    <tr key={i} aria-hidden="true">
                      <td colSpan={5} className="px-6 py-4"><div className="h-4 w-full animate-pulse rounded bg-gray-100" /></td>
                    </tr>
                  ))}
                {!loading && visibleUsers.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-gray-400">Geen gebruikers gevonden</td></tr>
                )}
                {visibleUsers.map((u) => (
                  <tr key={u.user_id} onClick={() => navigate(`/users/${u.user_id}`)} className="cursor-pointer hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <Link to={`/users/${u.user_id}`} onClick={(e) => e.stopPropagation()} className="hover:text-blue-700 hover:underline">
                        {fullName(u) || <span className="text-gray-400">(naamloos)</span>}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                    <td className="px-6 py-4"><RoleChips roles={u.roles} /></td>
                    <td className="px-6 py-4">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${u.is_active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}>
                        {u.is_active ? "Actief" : "Gedeactiveerd"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(u.last_login)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "invites" && canManageUsers && (
        <div className="overflow-x-auto bg-white shadow rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className={th}>E-mail</th>
                <th className={th}>Rollen</th>
                <th className={th}>Status</th>
                <th className={th}>Uitgenodigd door</th>
                <th className={th}>Verloopt</th>
                <th className={`${th} text-right`}>Acties</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invites === null && (
                <tr aria-hidden="true"><td colSpan={6} className="px-6 py-4"><div className="h-4 w-full animate-pulse rounded bg-gray-100" /></td></tr>
              )}
              {invites && invites.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-gray-400">Geen uitnodigingen</td></tr>
              )}
              {(invites || []).map((invite) => {
                const badge = INVITE_BADGES[invite.status] || INVITE_BADGES.pending;
                return (
                  <tr key={invite.id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{invite.email}</td>
                    <td className="px-6 py-4"><RoleChips roles={invite.roles} /></td>
                    <td className="px-6 py-4"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>{badge.label}</span></td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {invite.inviter_first_name && invite.inviter_last_name ? `${invite.inviter_first_name} ${invite.inviter_last_name}` : invite.inviter_email || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(invite.expires_at)}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                      {invite.status === "pending" && (
                        <button onClick={() => revokeInvite(invite.id)} className="font-medium text-red-600 hover:text-red-900">Intrekken</button>
                      )}
                      {(invite.status === "expired" || invite.status === "revoked") && (
                        <>
                          <button onClick={() => resendInvite(invite.id)} className="mr-3 font-medium text-blue-600 hover:text-blue-900">Nieuwe link</button>
                          <button onClick={() => setDeletingInvite(invite)} className="font-medium text-red-600 hover:text-red-900">Verwijderen</button>
                        </>
                      )}
                      {invite.status === "accepted" && (
                        <button onClick={() => setDeletingInvite(invite)} className="font-medium text-gray-500 hover:text-gray-800">Verwijderen</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
